import {
    stationSize, shieldDuration, asteroidClearRadius, enemyClass,
    worldWidth, worldHeight, worldIncrease, maxWorldSize,
    asteroidDensity, asteroidDensityDecrease, asteroidMinDensity
} from './constants';
import type { LaserImage } from './constants';
import { onAnyInputOnce } from './util';
import { SpaceScene } from './SpaceScene';
import { Asteroids } from './Asteroids';
import { Enemies } from './Enemies';
import { PlayerShip } from './PlayerShip';
import type { UIScene } from './UIScene';


export class Level extends SpaceScene {
    playerShip!: PlayerShip;
    asteroids!: Asteroids;
    enemies!: Enemies;
    station: Phaser.Physics.Arcade.Sprite;

    private debugGraphics!: Phaser.GameObjects.Graphics;
    private showDebug: boolean = false;
    // Ship sprite, input, polygon, prev-pose and ship sounds live on the
    // PlayerShip controller; asteroid/enemy polygons live on SpaceScene / Enemies.

    sfx_levelup: Phaser.Sound.BaseSound;
    sfx_gameOver: Phaser.Sound.BaseSound;

    laserInterval: number;
    level: number;
    shield: number;
    nextHit: number;
    alive: boolean;

    // The ship sprite lives on the PlayerShip controller; this getter keeps the
    // many `this.ship` / `level.ship` references (collisions, AI targeting,
    // debug) working unchanged.
    get ship(): Phaser.Physics.Arcade.Sprite {
        return this.playerShip.sprite;
    }

    constructor() {
        super('level');
    }

    create() {
        // Safely check for gamepad support
        if (this.input && this.input.gamepad && this.input.gamepad.on) {
            this.input.gamepad.on('connected', (pad) => {
                console.log('Gamepad connected:', pad.id);
            });
        } else {
            console.log('Gamepad support not available');
        }

        // Initialize debug graphics for collision polygons
        this.debugGraphics = this.add.graphics({ lineStyle: { width: 2, color: 0x00ff00 } });

        // Asteroid collision polygons (ship/enemy polygons are set up by their
        // own controllers).
        this.initAsteroidPolygons();

        // Show the HUD (it is hidden while on the splash screen).
        (this.scene.get('UIScene') as UIScene).setHudVisible(true);

        // Start the background music. It only plays in-game (the splash stops it)
        // and persists across level restarts, so only (re)start it if it isn't
        // already playing. On the very first game the audio may still be locked,
        // so start on the 'unlocked' event in that case.
        const music = this.registry.get('music') as any;
        if (music && !music.isPlaying) {
            if (this.sound.locked) {
                this.sound.once(Phaser.Sound.Events.UNLOCKED, () => music.play());
            } else {
                music.play();
            }
        }
    }

    updateUI() {
        (this.scene.get('UIScene') as UIScene).updateUI(this.level, this.shield);
    }

    init(data: {level: number}) {
        this.level = data.level;
        this.shield = 10;
        this.nextHit = this.time.now + shieldDuration;
        this.alive = true;

        //init world
        var dx = Math.min(maxWorldSize, worldWidth + data.level * worldIncrease);
        var dy = Math.min(maxWorldSize, worldHeight + data.level * worldIncrease);
        this.physics.world.setBounds(0, 0, dx, dy);

        //init sound (ship + enemy sounds are loaded by their controllers)
        this.sfx_levelup = this.sound.add('levelup');
        this.sfx_gameOver = this.sound.add('gameover');

        //init graphics
        var farback = this.add.tileSprite(0, 0, this.physics.world.bounds.width, this.physics.world.bounds.height, 'farback')
            .setOrigin(0, 0)
            .setScrollFactor(1);

        this.playerShip = new PlayerShip(this);

        this.asteroids = new Asteroids(this);

        this.enemies = new Enemies(this);
        this.setSfxVolume(this.sfxVol()); // apply the saved sfx volume (incl. enemy sounds)

        var stationLocation = [
            [stationSize, stationSize],
            [this.physics.world.bounds.width - stationSize, stationSize],
            [stationSize, this.physics.world.bounds.height - stationSize],
            [this.physics.world.bounds.width - stationSize, this.physics.world.bounds.height - stationSize]];

        var locationIndex = Phaser.Math.Between(0, stationLocation.length - 1);
        var x = stationLocation[locationIndex][0];
        var y = stationLocation[locationIndex][1];

        this.station = this.physics.add.staticSprite(x, y, 'station');
        this.station.setOrigin(0.5, 0.5);

        this.cameras.main.startFollow(this.ship);
        this.cameras.main.setBounds(0, 0, this.physics.world.bounds.width, this.physics.world.bounds.height);

        this.laserInterval = this.time.now;

        const area = this.physics.world.bounds.width * this.physics.world.bounds.height;
        const density = Math.max(asteroidMinDensity, asteroidDensity - this.level * asteroidDensityDecrease);
        const asteroids = area / density;
        for (let i = 0; i < asteroids; i++) {
            const location = this.getRandomLocation(asteroidClearRadius);
            this.asteroids.spawn(location.x, location.y);
        }

        for (let kind = 0; kind < enemyClass.length; kind++) {
            const count = enemyClass[kind].Count + this.level * enemyClass[kind].Increase;
            for (let i = 0; i < count; i++) {
                const location = this.getRandomLocation(enemyClass[kind].ClearRadius);
                this.enemies.spawn(location.x, location.y, kind);
            }
        }

        // Debug toggle for showing collision polygons
        const debugKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
        debugKey.on('down', () => {
            this.showDebug = !this.showDebug;
            this.debugGraphics.clear();
        });

        // P or Esc pauses the game (opens the pause / volume overlay).
        this.input.keyboard.on('keydown-P', () => this.pauseGame());
        this.input.keyboard.on('keydown-ESC', () => this.pauseGame());

        // Auto-pause when the player switches to another window (BLUR) or tab
        // (HIDDEN), so they don't come back to a ship that died while away.
        // (Phaser already suspends audio on blur; this freezes the game too.)
        // Resume is manual, via the pause overlay. Listeners live on the global
        // game emitter, so remove them when this scene shuts down (level restart)
        // to avoid stacking a fresh pair every time.
        const autoPause = () => this.pauseGame();
        this.game.events.on(Phaser.Core.Events.BLUR, autoPause);
        this.game.events.on(Phaser.Core.Events.HIDDEN, autoPause);
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            this.game.events.off(Phaser.Core.Events.BLUR, autoPause);
            this.game.events.off(Phaser.Core.Events.HIDDEN, autoPause);
        });

        this.updateUI();
    }

    getRandomLocation(clearRadius: number) {
        do {
            var x = Phaser.Math.Between(0, this.physics.world.bounds.width);
            var y = Phaser.Math.Between(0, this.physics.world.bounds.height);
        } while (Phaser.Math.Distance.Between(x, y, this.ship.x, this.ship.y) < clearRadius);

        return { x: x, y: y };
    }

    // Does a shot touch the given polygon? The bolt is modelled as a short
    // segment along its travel axis (tail to tip), so an elongated shot
    // registers as soon as its nose reaches the shape. Returns true if the tip
    // is inside the polygon, or if the segment crosses any of its edges.
    shotHitsPolygon(laser: Phaser.Physics.Arcade.Image, polygon: Phaser.Geom.Polygon): boolean {
        const halfLength = laser.width / 2;
        const cos = Math.cos(laser.rotation);
        const sin = Math.sin(laser.rotation);
        const tipX = laser.x + cos * halfLength;
        const tipY = laser.y + sin * halfLength;
        const tailX = laser.x - cos * halfLength;
        const tailY = laser.y - sin * halfLength;

        if (Phaser.Geom.Polygon.Contains(polygon, tipX, tipY)) {
            return true;
        }

        const shot = new Phaser.Geom.Line(tailX, tailY, tipX, tipY);
        const edge = new Phaser.Geom.Line();
        const points = polygon.points;
        for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];
            edge.setTo(p1.x, p1.y, p2.x, p2.y);
            if (Phaser.Geom.Intersects.LineToLine(shot, edge)) {
                return true;
            }
        }

        return false;
    }

    removeLaser(laser: LaserImage) {
        if (laser.removeTimer) {
            laser.removeTimer.remove(false);
        }
        laser.disableBody(true, true);
        if (this.playerShip.lasers.contains(laser))
            this.playerShip.lasers.remove(laser, true, true);
        else
            this.enemies.fireGroup.remove(laser, true, true);
    }

    fireHit(enemyfire: Phaser.Physics.Arcade.Image, laser: Phaser.Physics.Arcade.Image) {
        // Deactivate both projectiles
        this.removeLaser(enemyfire as LaserImage);
        this.removeLaser(laser as LaserImage);
    }

    gameOver() {
        // Stop the shield bar's low-shield flashing once the run is over.
        (this.scene.get('UIScene') as UIScene).onGameOver();

        const camera = this.cameras.main;
        const screenCenterX = camera.worldView.x + camera.width / 2;
        const screenCenterY = camera.worldView.y + camera.height / 2;

        const text = this.add.text(screenCenterX, screenCenterY, "GAME OVER", {
            fontFamily: 'Arial',
            fontSize: '65px',
            color: '#ffffff',
            align: 'center'
        });
        text.setOrigin(0.5);

        // Stop the background music right away so death feels final (the gameover
        // sound still plays, and the menu stays quiet). The next game restarts it.
        const music = this.registry.get('music') as any;
        if (music) {
            music.stop();
        }
        this.sfx_gameOver.play();

        // After a short pause, invite the player to return to the splash screen
        // and start again. (The pause avoids skipping it with a key still held
        // from the final moments of play.)
        this.time.delayedCall(900, () => {
            const prompt = this.add.text(screenCenterX, screenCenterY + 70, "Press any key to continue", {
                fontFamily: 'Arial',
                fontSize: '24px',
                color: '#ffd34d',
                align: 'center'
            });
            prompt.setOrigin(0.5);
            this.tweens.add({ targets: prompt, alpha: 0.2, duration: 700, yoyo: true, repeat: -1 });

            onAnyInputOnce(this, () => {
                this.sound.stopAll();
                this.scene.start('splash');
            });
        });
    }

    levelUp(ship, station) {
        // Stop the looping ship sounds, but leave the background music playing so
        // it carries seamlessly into the next level (the scene restarts below).
        this.playerShip.silence();
        this.sound.play('levelup', { volume: this.sfxVol() });

        const nextLevel = this.level + 1;

        this.scene.restart({ level: nextLevel });
    }

    update(time: number, delta: number) {
        if (this.alive) {
            this.playerShip.handleInput(time);
            this.handlePhysics();
            this.enemies.update(time);

            // Draw debug polygons if enabled
            if (this.showDebug) {
                this.debugPolygons();
            }

            // Remember the ship's pose so next frame's collision check can sweep
            // the gap between the two poses (anti-tunnelling).
            this.playerShip.recordPose();
        }
    }

    // Debug visualization for collision polygons
    debugPolygons() {
        this.debugGraphics.clear();

        if (!this.ship || !this.ship.active) return;

        // Draw ship polygon
        const shipPoly = this.playerShip.getShipPolygon(this.ship);
        this.debugGraphics.lineStyle(2, 0x00ff00);
        this.debugGraphics.strokePoints(shipPoly.points, true);

        // Draw asteroid polygons for nearby asteroids
        this.asteroids.group.getChildren().forEach((asteroid: Phaser.Physics.Arcade.Image) => {
            const distance = Phaser.Math.Distance.Between(
                this.ship.x, this.ship.y, asteroid.x, asteroid.y);

            // Only draw nearby asteroids (within 300 pixels)
            if (distance < 300) {
                const asteroidPoly = this.getAsteroidPolygon(asteroid);
                this.debugGraphics.lineStyle(2, 0xff0000);
                this.debugGraphics.strokePoints(asteroidPoly.points, true);
            }
        });

        // Draw enemy polygons (cyan)
        this.enemies.group.getChildren().forEach((enemy: Phaser.Physics.Arcade.Sprite) => {
            if (!enemy.active) return;
            const enemyPoly = this.enemies.getEnemyPolygon(enemy);
            this.debugGraphics.lineStyle(2, 0x00ffff);
            this.debugGraphics.strokePoints(enemyPoly.points, true);
        });
    }

    handlePhysics() {
        // Asteroid collisions (asteroid-vs-asteroid is a persistent overlap set
        // up by the Asteroids controller, so it is not repeated here).
        this.physics.overlap(this.playerShip.lasers, this.asteroids.group, this.asteroids.collision, this.asteroids.laserHitsAsteroid, this.asteroids);
        this.physics.overlap(this.asteroids.group, this.enemies.group, this.enemies.asteroidEnemyCollision, null, this.enemies);
        this.physics.collide(this.asteroids.group, this.station);

        // Ship collisions - using precise collision detection for ship-asteroid
        if (this.alive) {
            // Detect ship/asteroid contact with the precise (swept) polygon
            // test, then bounce and damage the ship ourselves in the callback.
            // Overlap (not collide) because we handle the separation manually.
            this.physics.overlap(this.asteroids.group, this.ship, this.playerShip.shipAsteroidBounce, this.playerShip.preciseShipCollision, this.playerShip);

            // Ship vs enemy ship: precise (swept) polygon detection, then we
            // bounce both manually - same approach as ship vs asteroid.
            this.physics.overlap(this.enemies.group, this.ship, this.playerShip.shipEnemyBounce, this.playerShip.preciseEnemyCollision, this.playerShip);
            this.physics.overlap(this.ship, this.station, this.levelUp, null, this);
        }

        // Enemy collisions
        this.physics.collide(this.enemies.group, this.station);
        this.physics.overlap(this.playerShip.lasers, this.enemies.group, this.enemies.enemyShot, this.enemies.laserHitsEnemy, this.enemies);

        // Enemy fire collisions
        this.physics.overlap(this.enemies.fireGroup, this.asteroids.group, this.asteroids.collision, this.asteroids.laserHitsAsteroid, this.asteroids);
        this.physics.overlap(this.enemies.fireGroup, this.ship, this.playerShip.enemyHit, null, this.playerShip);
        this.physics.overlap(this.enemies.fireGroup, this.playerShip.lasers, this.fireHit, null, this);
    }

    // Current sound-effects volume (0-1) from the global registry. Used for the
    // sounds that are played with an explicit volume.
    sfxVol(): number {
        const v = this.registry.get('sfxVolume');
        return (v === undefined || v === null) ? 1 : v;
    }

    // Apply the sound-effects volume to all SFX sound objects, so the ones played
    // without an explicit volume (laser, explosions, collisions...) use it.
    setSfxVolume(volume: number) {
        const sounds: any[] = [this.sfx_levelup, this.sfx_gameOver];
        for (let i = 0; i < sounds.length; i++) {
            if (sounds[i]) {
                sounds[i].setVolume(volume);
            }
        }
        this.playerShip?.setSfxVolume(volume);
        this.enemies?.setSfxVolume(volume);
    }

    // Pause the game and open the pause overlay (volume controls + resume).
    pauseGame() {
        // Idempotent: do nothing if the game is over or already paused. This lets
        // the keyboard, gamepad and auto-pause-on-blur paths all call it safely.
        if (!this.alive || this.scene.isActive('pause')) {
            return;
        }
        // Stop the looping ship sounds so they don't drone on while paused.
        this.playerShip.silence();
        // Pause the background music too (resumed in Pause.resumeGame). Covers
        // every pause path - P/Esc, the gamepad Start button, and auto-pause on
        // window blur / tab switch.
        const music = this.registry.get('music') as any;
        if (music && music.isPlaying) {
            music.pause();
        }
        this.scene.launch('pause');
        this.scene.pause();
    }

}
